import { makeStyles } from '@material-ui/styles'
import ApiClient from 'api-client/ApiClient'
import { listTablePrefs } from 'app/constants'
import { ArrayElement } from 'core/actions/Action'
import ListContainer from 'core/containers/ListContainer'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { GridBatchActionSpec } from 'core/elements/grid/hooks/useGridSelectableRows'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import DataKeys from 'k8s/DataKeys'
import { pick } from 'ramda'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { formatTimestampToLocal } from '../../vms/helpers'
import { listNetworks } from '../actions'
import { networksSelector, subnetsFromAllNetworksSelector } from '../selectors'
import CreateRouterInterfaceModal from './CreateRouterInterfaceModal'
import DeleteRouterInterfaceDialog from './DeleteRouterInterfaceDialog'

type ModelDataKey = DataKeys.OpenstackPorts
type SelectorModel = ArrayElement<ReturnType<any>>

const defaultParams = {
  orderBy: 'created_at',
  orderDirection: 'desc',
}

const useStyles = makeStyles<Theme>((theme) => ({
  interfaces: {
    marginTop: '16px',
  },
}))

const columns: GridViewColumn<SelectorModel>[] = [
  {
    key: 'subnetLabel',
    label: 'Subnet',
  },
  {
    key: 'name',
    label: 'Port',
    render: (value) => ((value || '-') as unknown) as React.ReactNode,
  },
  {
    key: 'staticIp',
    label: 'Private IP',
  },
  {
    key: 'status',
    label: 'Status',
  },
  {
    key: 'device_owner',
    label: 'Type',
  },
  {
    key: 'admin_state_up',
    label: 'Admin State',
    render: (state) => (state ? 'Up' : 'Down'),
  },
  {
    key: 'created_at',
    label: 'Created',
    render: (time: string) => formatTimestampToLocal(time),
    display: false,
  },
]

const usePrefParams = createUsePrefParamsHook('RouterInterfaces', listTablePrefs)

const { neutron } = ApiClient.getInstance()

export default function RouterInterfacesPage({ router, loading }) {
  const classes = useStyles()
  const { params, getParamsUpdater } = usePrefParams(defaultParams)
  const [selectedInterface, setSelectedInterface] = useState(null)
  const [showDeleteInterfaceModal, setShowDeleteInterfaceModal] = useState(false)
  const [loadingRouterInterfaces, setLoadingRouterInterfaces] = useState(false)
  const [routerInterfaces, setRouterInterfaces] = useState([])
  const {
    message: networksMessage,
    loading: loadingNetworks,
    reload: reloadNetworks,
  } = useListAction(listNetworks, {
    params,
  })
  const networks = useSelector(networksSelector)

  const getRouterInterfaces = useCallback(async () => {
    try {
      setLoadingRouterInterfaces(true)
      const ports = await neutron.getPortsForRouter({ routerId: router.id })
      const interfaces = ports
        .map((port) => {
          const matchingNetwork = networks.find((network) => network.id === port?.network_id)
          const matchingSubnet = matchingNetwork?.subnetDetails?.find(
            (subnet) => subnet.id === port?.fixed_ips?.[0]?.subnet_id,
          )
          return {
            ...port,
            staticIp: port?.fixed_ips?.[0]?.ip_address,
            subnetLabel: `${matchingNetwork?.name}: ${matchingSubnet?.name}`,
          }
        })
        .filter((port) => port.device_owner)
      setRouterInterfaces(interfaces)
    } catch (error) {
      setRouterInterfaces([])
    } finally {
      setLoadingRouterInterfaces(false)
    }
  }, [router, networks])

  useEffect(() => {
    if (!router?.id) {
      return
    }

    getRouterInterfaces()
  }, [router, getRouterInterfaces])

  const batchActions: GridBatchActionSpec<SelectorModel>[] = [
    {
      cond: (port) => {
        return !!port
      },
      disabledTooltip: 'Select at least one interface to delete',
      label: 'Delete Interface',
      icon: 'trash-alt',
      handleAction: (ports) => {
        setSelectedInterface(ports)
        setShowDeleteInterfaceModal(true)
      },
      refreshAfterSuccess: true,
    },
  ]

  return (
    <article className={classes.interfaces}>
      <CreateRouterInterfaceModal
        addRoute={routeManager.routes.openstack.createRouterInterface}
        router={router}
      />
      {showDeleteInterfaceModal && (
        <DeleteRouterInterfaceDialog
          onClose={() => setShowDeleteInterfaceModal(false)}
          rows={selectedInterface}
        />
      )}
      <ListContainer<ModelDataKey, SelectorModel>
        label="Interfaces"
        showBreadcrumbs={false}
        dataKey={DataKeys.OpenstackPorts}
        searchTargets={['name']}
        uniqueIdentifier={'id' as keyof SelectorModel}
        loading={loading || loadingNetworks || loadingRouterInterfaces}
        loadingMessage={networksMessage || 'Loading Interfaces...'}
        onRefresh={() => {
          getRouterInterfaces()
          reloadNetworks(true, true)
        }}
        data={routerInterfaces}
        columns={columns}
        addUrl={routeManager.routes.openstack.createRouterInterface.path({ id: router?.id })}
        addText="Create Interface"
        getParamsUpdater={getParamsUpdater}
        batchActions={batchActions}
        multiSelection
        {...pick(listTablePrefs, params)}
      />
    </article>
  )
}
